import { FC, useEffect } from 'react';
import { Button, Divider } from 'antd';

import { useEntitlements } from '@/hooks/useEntitlements';
import PlatformAlert from '@/shared/components/alerts/PlatformAlert';
import SuccessfulCheckoutAlert from '@/shared/components/alerts/SuccessfulCheckoutAlert';
import { BaseSkeleton } from '@/shared/components/skeletons';
import { useBillingStore } from '@/stores/database/aliases/billing';

import Heading from './layout/01-Heading';
import Summary from './layout/02-Summary';
import InvoiceBreakdown from './layout/03-InvoiceBreakdown';
import BillingPortal from './layout/04-BillingPortal';

const Billing: FC = (): JSX.Element => {
    const { snapshot, fetchSnapshot, loading, error } = useBillingStore();
    const { planTier } = useEntitlements();

    useEffect(() => {
        fetchSnapshot();
        const refresh = () => void fetchSnapshot({ force: true });
        window.addEventListener('focus', refresh);
        return () => window.removeEventListener('focus', refresh);
    }, [fetchSnapshot]);

    if (loading) {
        return <BaseSkeleton active round paragraph={{ rows: 16 }} />;
    }

    if (error || !snapshot) {
        return (
            <PlatformAlert
                type='error'
                showIcon
                message='Billing Could Not Be Loaded'
                description='Your billing details are temporarily unavailable. Retry to load the current plan.'
                action={<Button onClick={() => void fetchSnapshot({ force: true })}>Retry</Button>}
            />
        );
    }

    const isFree = planTier === 'free';
    const isEnterprise = planTier === 'enterprise';

    const seatPlan = snapshot.plan?.name ?? 'Free';
    const seatPrice = snapshot.seats?.pricePerSeat ?? 0;
    const seats = snapshot.seats?.purchasedSeats ?? 0;
    // const total = snapshot?.invoices?.nextInvoiceSummary?.amountDue ?? 0;
    const total = snapshot?.invoices?.nextInvoiceSummary?.normalizedBillingCycleAmountDue ?? 0;

    return !loading ? (
        <>
            <Heading snapshot={snapshot} />
            <Divider style={{ margin: '1rem 0' }} />
            <Summary
                total={total}
                interval={snapshot.subscription?.billingCycle ?? ''}
                nextBillDate={snapshot.invoices?.upcoming?.next_payment_attempt}
                isFree={isFree}
                isEnterprise={isEnterprise}
            />
            <Divider style={{ margin: '1rem 0' }} />
            <InvoiceBreakdown
                isFree={isFree}
                isEnterprise={isEnterprise}
                seats={seats}
                seatPlan={seatPlan}
                seatPrice={seatPrice}
                interval={snapshot.subscription?.billingCycle ?? ''}
                addons={snapshot.addOns ?? []}
            />
            {!isEnterprise && <BillingPortal />}
            <SuccessfulCheckoutAlert />
        </>
    ) : loading ? (
        <BaseSkeleton active round paragraph={{ rows: 16 }} />
    ) : (
        <>Billing Not Available</>
    );
};

export default Billing;
